import { useState } from "react";
import { Link } from "wouter";
import { Bookmark, Package, Wrench, Building2, Users, Briefcase, FolderKanban, ReceiptText, Search, ChevronRight, Loader2, type LucideIcon } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { isNativeApp } from "../lib/platform";
import { MobileAccountBreadcrumb } from "./MobileAccountBreadcrumb";
import "./MobileSavedItems.css";

/** Resolved saved references from the existing /saved API; the listing itself is unchanged. */
export type SavedItemReference = { kind: string; refId: string; title: string; subtitle: string; link: string };

type MobileSavedItemsProps = {
  items: SavedItemReference[] | null;
  error: string | null;
  busyKey: string | null;
  canUndo: boolean;
  onRemove: (item: SavedItemReference) => void;
  onUndo: () => void;
  onRetry: () => void;
};

const KIND_META: Record<string, { Icon: LucideIcon; label: string; color: string }> = {
  item: { Icon: Package, label: "item", color: "gold" },
  service: { Icon: Wrench, label: "service", color: "green" },
  company: { Icon: Building2, label: "company", color: "gold" },
  person: { Icon: Users, label: "person", color: "blue" },
  profile: { Icon: Users, label: "profile", color: "blue" },
  job: { Icon: Briefcase, label: "job", color: "red" },
  project: { Icon: FolderKanban, label: "project", color: "blue" },
  rfq: { Icon: ReceiptText, label: "rfq", color: "purple" },
};

export function MobileSavedItems({ items, error, busyKey, canUndo, onRemove, onUndo, onRetry }: MobileSavedItemsProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const availableKinds = [...new Set((items ?? []).map((item) => item.kind))];
  // Removing the last item of a kind must not strand the list on an unavailable filter.
  const selectedKind = availableKinds.includes(kind) ? kind : "all";
  const label = (value: string) => KIND_META[value] ? t(`saved.mobile.${KIND_META[value].label}`) : value;
  const visibleItems = (items ?? []).filter((item) => (selectedKind === "all" || item.kind === selectedKind)
    && `${item.title} ${item.subtitle} ${label(item.kind)}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  return (
    <div className="mobile-saved-items sm:hidden">
      <MobileAccountBreadcrumb currentLabel={t("saved.mobile.title")} count={items?.length ?? 0} />
      {error && <div className="mobile-saved-error" role="alert"><p>{error}</p>{!isNativeApp() && <button type="button" onClick={onRetry}>{t("saved.mobile.retry")}</button>}</div>}
      {canUndo && <div className="mobile-saved-undo" role="status"><span>{t("saved.mobile.removed")}</span><button type="button" disabled={busyKey !== null} onClick={onUndo}>{t("saved.mobile.undo")}</button></div>}
      {items === null ? error ? null : <div className="mobile-saved-loading" role="status"><Loader2 className="animate-spin" aria-hidden="true" /><span>{t("saved.mobile.loading")}</span></div>
        : items.length === 0 ? <MobileSavedEmpty /> : <>
          <div className="mobile-saved-toolbar"><label className="mobile-saved-query"><Search aria-hidden="true" /><input type="search"
            value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("saved.mobile.search")} aria-label={t("saved.mobile.search")} /></label></div>
          <nav className="mobile-saved-kinds" aria-label={t("saved.mobile.categories")}>
            {["all", ...availableKinds].map((value) => <button type="button" key={value} aria-pressed={selectedKind === value} onClick={() => setKind(value)}>
              {value === "all" ? t("saved.mobile.all") : label(value)}</button>)}
          </nav>
          <div className="mobile-saved-list">{visibleItems.map((item) => <MobileSavedRow key={`${item.kind}:${item.refId}`} item={item}
            busyKey={busyKey} onRemove={onRemove} />)}
            {visibleItems.length === 0 && <div className="mobile-saved-no-match"><p>{t("saved.mobile.noMatch")}</p>
              <button type="button" onClick={() => { setKind("all"); setQuery(""); }}>{t("saved.mobile.clear")}</button></div>}
          </div>
        </>}
    </div>
  );
}

function MobileSavedEmpty() {
  const { t } = useI18n();
  const shortcuts = [
    { key: "person", href: "/jobseekers" }, { key: "service", href: "/services" },
    { key: "job", href: "/jobs" }, { key: "item", href: "/marketplace?tab=Marketplace" },
  ];
  return <>
    <section className="mobile-saved-banner"><Bookmark aria-hidden="true" /><div><h2>{t("saved.mobile.emptyTitle")}</h2><p>{t("saved.mobile.emptyBody")}</p></div></section>
    <h2 className="mobile-saved-category-heading">{t("saved.mobile.categories")}</h2>
    <div className="mobile-saved-shortcuts">{shortcuts.map(({ key, href }) => {
      const meta = KIND_META[key];
      return <Link key={key} href={href} className={`mobile-saved-shortcut ${meta.color}`}><span><meta.Icon aria-hidden="true" />{t(`saved.mobile.${meta.label}`)}</span><ChevronRight aria-hidden="true" /></Link>;
    })}</div>
    <p className="mobile-saved-tip">{t("saved.mobile.tip")}</p>
  </>;
}

function MobileSavedRow({ item, busyKey, onRemove }: { item: SavedItemReference; busyKey: string | null; onRemove: (item: SavedItemReference) => void }) {
  const { t } = useI18n();
  const meta = KIND_META[item.kind] ?? { Icon: Bookmark, label: "", color: "blue" };
  const isBusy = busyKey === `${item.kind}:${item.refId}`;
  return <article className="mobile-saved-row">
    <Link href={item.link} className="mobile-saved-row-main"><span className={`mobile-saved-icon ${meta.color}`}><meta.Icon aria-hidden="true" /></span>
      <span className="mobile-saved-row-copy"><strong>{item.title}</strong><small>{meta.label ? t(`saved.mobile.${meta.label}`) : item.kind}{item.subtitle ? ` · ${item.subtitle}` : ""}</small></span></Link>
    <button type="button" className="mobile-saved-remove" disabled={busyKey !== null} onClick={() => onRemove(item)} aria-label={`${t("saved.mobile.remove")}: ${item.title}`}>
      {isBusy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Bookmark aria-hidden="true" />}</button>
  </article>;
}
